import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { StageBadge, PriorityBadge, StatusBadge } from "@/components/Badges";
import { DEPARTMENTS, deptLabel } from "@/lib/constants";
import {
  FileText,
  Flame,
  CircleCheck,
  Timer,
  AlertTriangle,
  History,
  ArrowUpRight,
  Building2,
  Layers,
} from "lucide-react";
import { format } from "date-fns";

function StatCard({ label, value, icon: Icon, accent, sub, testId, onClick }) {
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      data-testid={testId}
      onClick={onClick}
      className={`bg-[hsl(var(--surface))] rounded-lg border border-border p-5 transition-all text-left w-full ${
        onClick
          ? "hover:border-accent hover:shadow-sm hover:-translate-y-0.5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          : "hover:border-accent/30"
      }`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            {label}
          </div>
          <div className="mt-3 text-3xl font-bold text-foreground tracking-tight tabular-nums">
            {value}
          </div>
          {sub && <div className="text-xs text-muted-foreground mt-1.5">{sub}</div>}
        </div>
        <div
          className="w-10 h-10 rounded-md flex items-center justify-center"
          style={{ backgroundColor: `${accent}22`, color: accent }}
        >
          <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
      </div>
    </Wrapper>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const { t, lang } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    setLoadError(null);
    api
      .get("/stats")
      .then((r) => alive && setStats(r.data))
      .catch((e) => alive && setLoadError(e?.message || "Failed to load"));
    return () => {
      alive = false;
    };
  }, [attempt]);

  const deptEntries = useMemo(() => {
    if (!stats) return [];
    return DEPARTMENTS.map((d) => [d, stats.department_wise?.[d] || 0]).sort(
      (a, b) => b[1] - a[1]
    );
  }, [stats]);

  if (loadError && !stats) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="text-sm text-muted-foreground">
          Could not load dashboard.
        </div>
        <button
          onClick={() => setAttempt((n) => n + 1)}
          className="mt-3 text-sm font-semibold text-accent hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-[hsl(var(--panel-alt))] rounded" />
        <div className="grid grid-cols-4 gap-6">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-28 bg-[hsl(var(--surface))] rounded-lg border border-border" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-accent font-semibold mb-2">
            {t("dash.overview")} · {format(new Date(), "EEEE, dd MMM yyyy")}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight">
            {t("dash.title")}
          </h1>
          <p className="text-muted-foreground text-sm mt-2">{t("dash.subtitle")}</p>
        </div>
        <Link
          to="/complaints/new"
          data-testid="dashboard-new-complaint"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-accent hover:bg-accent/90 text-accent-foreground text-sm font-semibold transition-colors"
        >
          <FileText className="w-4 h-4" />
          {t("dash.new_btn")}
        </Link>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          testId="stat-total"
          label={t("dash.total")}
          value={stats.total}
          icon={Layers}
          accent="#0f172a"
          sub={t("dash.all_time")}
          onClick={() => navigate("/complaints")}
        />
        <StatCard
          testId="stat-open"
          label={t("dash.open")}
          value={stats.open}
          icon={Flame}
          accent="#059669"
          sub={t("dash.active")}
          onClick={() => navigate("/complaints?status=Open")}
        />
        <StatCard
          testId="stat-closed"
          label={t("dash.closed")}
          value={stats.closed}
          icon={CircleCheck}
          accent="#475569"
          sub={t("dash.resolved")}
          onClick={() => navigate("/complaints?status=Closed")}
        />
        <StatCard
          testId="stat-today"
          label={t("dash.today")}
          value={stats.today}
          icon={Timer}
          accent="#ea580c"
          sub={t("dash.filed_today")}
          onClick={() =>
            navigate(
              `/complaints?date_from=${new Date().toISOString().slice(0, 10)}`
            )
          }
        />
        <StatCard
          testId="stat-overdue"
          label={t("dash.overdue")}
          value={stats.overdue}
          icon={AlertTriangle}
          accent="#dc2626"
          sub={t("dash.past_action")}
          onClick={() => navigate("/complaints?overdue=1&status=Open")}
        />
        <StatCard
          testId="stat-departments"
          label={t("dash.departments")}
          value={DEPARTMENTS.length}
          icon={Building2}
          accent="#7c3aed"
          sub={t("dash.service_lines")}
          onClick={() => navigate("/departments")}
        />
        <StatCard
          testId="stat-stages"
          label={t("dash.stages")}
          value={Object.keys(stats.stage_wise || {}).length}
          icon={History}
          accent="#2563eb"
          sub={t("dash.in_use")}
          onClick={() => navigate("/complaints")}
        />
        <StatCard
          testId="stat-recent"
          label={t("dash.recent")}
          value={(stats.recently_updated || []).length}
          icon={ArrowUpRight}
          accent="#d97706"
          sub={t("dash.last_5")}
          onClick={() =>
            navigate("/complaints?sort_by=updated_at&sort_dir=-1")
          }
        />
      </div>

      {/* Two columns: dept + stage */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[hsl(var(--surface))] border border-border rounded-lg">
          <div className="flex items-center justify-between p-5 border-b border-border">
            <div>
              <h2 className="text-lg font-bold text-foreground tracking-tight">
                {t("dash.dept_volume")}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t("dash.dept_click_hint")}
              </p>
            </div>
            <Link
              to="/departments"
              className="text-xs font-semibold text-accent hover:underline"
            >
              {t("dash.view_all")}
            </Link>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[440px] overflow-auto">
            {deptEntries.map(([name, count]) => (
              <button
                key={name}
                data-testid={`dashboard-dept-${name}`}
                onClick={() =>
                  navigate(`/complaints?department=${encodeURIComponent(name)}`)
                }
                className="group flex items-center justify-between p-3 rounded-md border border-border hover:border-accent/40 hover:bg-accent/5 transition-colors text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-8 h-8 rounded-md bg-[hsl(var(--panel-alt))] group-hover:bg-accent/10 text-foreground flex items-center justify-center flex-shrink-0"
                  >
                    <Building2 className="w-4 h-4" />
                  </div>
                  <div className={`text-sm truncate text-foreground ${lang === "hi" ? "font-devanagari" : ""}`}>
                    {deptLabel(name, lang)}
                  </div>
                </div>
                <div className="font-mono-cms font-bold text-foreground text-sm tabular-nums pl-2">
                  {count}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-[hsl(var(--surface))] border border-border rounded-lg">
          <div className="p-5 border-b border-border">
            <h2 className="text-lg font-bold text-foreground tracking-tight">
              {t("dash.stage_dist")}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("dash.stage_hint")}
            </p>
          </div>
          <div className="p-5 space-y-2">
            {Object.entries(stats.stage_wise || {})
              .sort((a, b) => b[1] - a[1])
              .map(([stage, count]) => (
                <button
                  key={stage}
                  data-testid={`stage-row-${stage}`}
                  onClick={() =>
                    navigate(`/complaints?stage=${encodeURIComponent(stage)}`)
                  }
                  className="w-full flex items-center justify-between py-1.5 px-1 rounded hover:bg-[hsl(var(--panel-alt))] transition-colors"
                >
                  <StageBadge stage={stage} />
                  <span className="font-mono-cms font-bold text-foreground text-sm tabular-nums">
                    {count}
                  </span>
                </button>
              ))}
            {Object.keys(stats.stage_wise || {}).length === 0 && (
              <div className="text-sm text-muted-foreground py-6 text-center">
                No stage data yet.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recently updated table */}
      <div className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-hidden">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-foreground tracking-tight">
              {t("dash.recent")}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("dash.recent_hint")}
            </p>
          </div>
          <Link
            to="/complaints"
            className="text-xs font-semibold text-accent hover:underline"
          >
            {t("dash.view_all")}
          </Link>
        </div>
        <table className="w-full">
          <thead className="bg-[hsl(var(--panel-alt))] border-b border-border">
            <tr className="text-left text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
              <th className="py-3 px-5">Complaint No.</th>
              <th className="py-3 px-5">Applicant</th>
              <th className="py-3 px-5">Department</th>
              <th className="py-3 px-5">Stage</th>
              <th className="py-3 px-5">Status</th>
              <th className="py-3 px-5">Priority</th>
              <th className="py-3 px-5">Updated</th>
            </tr>
          </thead>
          <tbody>
            {(stats.recently_updated || []).map((c) => (
              <tr
                key={c.id}
                data-testid={`recent-row-${c.id}`}
                onClick={() => navigate(`/complaints/${c.id}`)}
                className="border-b border-border hover:bg-[hsl(var(--panel-alt))] cursor-pointer"
              >
                <td className="py-3 px-5 font-mono-cms text-xs text-foreground font-semibold">
                  {c.complaint_no}
                </td>
                <td className="py-3 px-5 text-sm text-foreground">
                  <div className="font-semibold">{c.applicant_name}</div>
                  <div className="text-xs text-muted-foreground font-mono-cms">
                    {c.mobile}
                  </div>
                </td>
                <td className={`py-3 px-5 text-sm text-foreground/80 ${lang === "hi" ? "font-devanagari" : ""}`}>
                  {deptLabel(c.department, lang)}
                </td>
                <td className="py-3 px-5">
                  <StageBadge stage={c.current_stage} />
                </td>
                <td className="py-3 px-5">
                  <StatusBadge status={c.status} />
                </td>
                <td className="py-3 px-5">
                  <PriorityBadge priority={c.priority} />
                </td>
                <td className="py-3 px-5 text-xs text-muted-foreground">
                  {format(new Date(c.updated_at), "dd MMM, HH:mm")}
                </td>
              </tr>
            ))}
            {(stats.recently_updated || []).length === 0 && (
              <tr>
                <td colSpan={7} className="py-10 text-center text-muted-foreground text-sm">
                  No recent updates.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
